import { Alert, Box, Button, ButtonBase, Card, Chip, Grid, Skeleton, Slider, Stack, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { apiPatch } from "../../../services/Api/apiCall";
import { fetchCollection, queryKeys } from "../../../services/ReactQuery/reactQueryService";
import HumanResource from "../../../types/HumanResource";
import { HydraCollectionResult } from "../../../types/HydraCollectionResult";
import styles from "./caseAssignmentPreferenceStyles";
import CaseAssignmentPreferenceSkeleton from "./CaseAssignmentPreferenceSkeleton";
import standard from "../../../assets/icon/bonus/standard.svg";
import standardBadge from "../../../assets/icon/bonus/standardBadge.svg";
import sportlich from "../../../assets/icon/bonus/sportlich.svg";
import insane from "../../../assets/icon/bonus/insane.svg";

const humanResourcesUri = "human_resources";
const preferences = [
  { value: 1, label: "Standard", description: "Gerne möglichst entspannt", icon: standard, badgeIcon: standardBadge },
  { value: 1.5, label: "Sportlich", description: "Challenge accepted!", icon: sportlich, badgeIcon: sportlich },
  { value: 2, label: "Insane", description: "YOLO - Vollgas und durch!", icon: insane, badgeIcon: insane },
];

type CaseAssignmentPreferenceFormProps = { humanResource: HumanResource };

function CaseAssignmentPreferenceForm({ humanResource }: CaseAssignmentPreferenceFormProps) {
  const queryClient = useQueryClient();
  const [draftWeight, setDraftWeight] = useState<number | null>(null);
  const savePreference = useMutation({
    mutationFn: (weight: number) => apiPatch<HumanResource>(humanResource["@id"], null, { caseAssignmentWeight: weight }),
    // React Query wartet auf das Promise, der Entwurf wird also erst nach dem Refetch zurückgesetzt.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.all(humanResourcesUri) }),
    onSettled: () => setDraftWeight(null),
  });
  const weight = draftWeight ?? humanResource.caseAssignmentWeight;
  const selectPreference = (value: number) => {
    if (value === humanResource.caseAssignmentWeight) {
      setDraftWeight(null);
      return;
    }
    setDraftWeight(value);
    savePreference.mutate(value);
  };

  return (
    <>
      <Box sx={styles.sliderContainer}>
        <Slider
          min={1}
          max={2}
          step={null}
          marks={preferences.map(({ value }) => ({ value }))}
          value={weight}
          name="caseAssignmentWeight"
          disabled={savePreference.isPending}
          onChange={(_, value) => setDraftWeight(value as number)}
          onChangeCommitted={(_, value) => selectPreference(value as number)}
          sx={styles.slider}
        />
      </Box>
      <Grid container spacing={2}>
        {preferences.map((preference) => (
          <Grid size={4} key={preference.value}>
            <ButtonBase
              onClick={() => selectPreference(preference.value)}
              disabled={savePreference.isPending}
              sx={[styles.option, weight === preference.value && styles.selectedOption]}
            >
              <Stack spacing={1} sx={{ alignItems: "center" }}>
                <Typography variant="subtitle1" sx={styles.optionLabel}>{preference.label}</Typography>
                <img src={preference.icon} alt="" />
                <Typography variant="caption" sx={styles.optionDescription}>{preference.description}</Typography>
              </Stack>
            </ButtonBase>
          </Grid>
        ))}
      </Grid>
      {savePreference.isError && <Alert severity="error" sx={{ marginTop: "1rem" }}>Die Zuteilungspräferenz konnte nicht gespeichert werden. Bitte erneut versuchen.</Alert>}
    </>
  );
}

export default function CaseAssignmentPreference() {
  const currentUser = useCurrentUser();
  const filter = { backofficeUser: currentUser.id };
  const { data: humanResource, isPending, isError, refetch } = useQuery({
    queryKey: queryKeys.collection(humanResourcesUri, filter),
    queryFn: (): Promise<HydraCollectionResult<HumanResource>> => fetchCollection(humanResourcesUri, filter),
    select: (result) => result["hydra:member"][0] ?? null,
  });
  const activePreference = preferences.find((preference) => preference.value === humanResource?.caseAssignmentWeight);

  let content = <CaseAssignmentPreferenceSkeleton />;
  if (isError) {
    content = <Alert severity="error" action={<Button onClick={() => refetch()}>Erneut laden</Button>}>Die Zuteilungspräferenz konnte nicht geladen werden.</Alert>;
  } else if (humanResource) {
    content = <CaseAssignmentPreferenceForm humanResource={humanResource} />;
  } else if (!isPending) {
    content = <Alert severity="info">Für dieses Konto sind keine Personaldaten hinterlegt.</Alert>;
  }

  return (
    <Card sx={styles.card}>
      <Grid container spacing={2} sx={styles.heading}>
        <Grid>
          <Typography variant="h2" component="h2">Deine Präferenz für neue Fälle</Typography>
          <Typography sx={styles.subheading}>Wähle, wie häufig neue Fälle bei dir landen.</Typography>
        </Grid>
        <Grid>
          {isPending && <Skeleton animation="wave" variant="rounded" width="9rem" height="2.25rem" />}
          {activePreference && (
            <Chip label={`${activePreference.label} · aktiv`} icon={<img src={activePreference.badgeIcon} alt="" />} sx={styles.badge} />
          )}
        </Grid>
      </Grid>
      {content}
    </Card>
  );
}
